import { useState, useEffect, useMemo } from 'react';
import { Heart, MapPin, Briefcase, GraduationCap, Check, X, ChevronLeft, ChevronRight, Star, Users, Phone, Mail, MessageCircle, UserPlus, Bookmark, Share2, MoreVertical, Cake, Home, Flag, ArrowLeft, Crown, Eye, Shield, Calendar, User, Lock } from 'lucide-react';
import { usePhotoBlur } from '../hooks/usePhotoBlur';
import { ReportUserModal } from './ReportUserModal';
import { ContactUpgradeModal } from './ContactUpgradeModal';
import { Button } from './ui/button';
import { formatMaritalStatus, formatEducation } from '../utils/profileFormatters';
import { toast } from 'sonner';
import axios from 'axios';
import { API_URL } from '../lib/api-client';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { logger } from '../utils/logger';
import { generateProfileSlug } from '../utils/seo';

const heightToFeetInches = (cm: number): string => {
  if (!cm || typeof cm !== 'number') return "5'5\"";

  // If the value is less than 10, it's likely already in feet (not cm)
  // Average human height in cm is 150-200, so anything < 10 is probably feet
  if (cm < 10) {
    // Treat as feet, no conversion needed
    return `${cm}'0"`;
  }

  const totalInches = Math.round(cm / 2.54);
  const feet = Math.floor(totalInches / 12);
  const inches = totalInches % 12;
  return `${feet}'${inches}"`;
};

// Helper function to format height from various formats
const formatHeightDisplay = (height: string | number): string => {
  if (!height) return "Not specified";

  // If it's already in feet/inches format (e.g., "5'7""), return as is
  if (typeof height === 'string' && height.includes("'")) {
    return height;
  }

  // If it's a string with "cm", extract the number
  if (typeof height === 'string' && height.includes('cm')) {
    const cmValue = parseInt(height.replace(/[^0-9]/g, ''));
    if (!isNaN(cmValue)) {
      return heightToFeetInches(cmValue);
    }
  }

  // If it's a number or numeric string, convert it
  const numericHeight = typeof height === 'number' ? height : parseInt(height);
  if (!isNaN(numericHeight)) {
    return heightToFeetInches(numericHeight);
  }

  // Fallback
  return height.toString();
};

interface Profile {
  id: string;
  _id?: string;
  userId?: any;
  name: string;
  age: number;
  height: string;
  location: string;
  city?: string;
  state?: string;
  education: string;
  occupation: string;
  company: string;
  income: string;
  religion: string;
  community?: string;
  caste?: string;
  image: string;
  isPro: boolean;
  isOnline: boolean;
  profileManaged: string;
  lastActive: string;
  about?: string;
  photos?: string[];
  isSponsored?: boolean;
  rawProfile?: any;
  dateOfBirth?: string;
  motherTongue?: string;
  maritalStatus?: string;
  familyType?: string;
  familyStatus?: string;
  familyValues?: string;
  fatherName?: string;
  fatherOccupation?: string;
  motherName?: string;
  motherOccupation?: string;
  siblings?: string | { brothers?: number; sisters?: number };
  diet?: string;
  smoking?: string;
  drinking?: string;
  partnerPreferences?: {
    ageRange?: { min?: number; max?: number };
    heightRange?: { min?: number; max?: number };
    maritalStatus?: string[];
    religion?: string[];
    community?: string[];
    motherTongue?: string[];
    diet?: string[];
    smoking?: string[];
    drinking?: string[];
  };
}

interface ViewProfilePageProps {
  profile: Profile;
  onBack: () => void;
  hideInterestButtons?: boolean;
  interestContext?: 'received' | 'sent' | 'accepted' | null;
  onNavigateToPlans?: () => void;
  onOpenChat?: (userId: string) => void;
  onViewProfile?: (profile: any) => void;
  onViewAllSimilar?: () => void;
}

export default function ViewProfilePageNew({
  profile,
  onBack,
  hideInterestButtons,
  onNavigateToPlans,
  onOpenChat,
  onViewProfile,
  onViewAllSimilar,
}: ViewProfilePageProps) {
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [activeTab, setActiveTab] = useState<'about' | 'career' | 'family' | 'preferences'>('about');
  const [currentUserProfile, setCurrentUserProfile] = useState<any>(null);
  const [showImageViewer, setShowImageViewer] = useState(false);
  const [isLiking, setIsLiking] = useState(false);
  const [isShortlisting, setIsShortlisting] = useState(false);
  const [isShortlisted, setIsShortlisted] = useState(false);
  const [contactRevealed, setContactRevealed] = useState(false);
  const [contactInfo, setContactInfo] = useState<any>(null);
  const [revealingContact, setRevealingContact] = useState(false);
  const [similarProfiles, setSimilarProfiles] = useState<any[]>([]);
  const [interactionStatus, setInteractionStatus] = useState<{ status: string; interactionId: string } | null>(null);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [showContactUpgradeModal, setShowContactUpgradeModal] = useState(false);
  const [contactErrorMessage, setContactErrorMessage] = useState<string>('');

  const handleShareProfile = async () => {
    try {
      const displayName = profile?.name || 'User Profile';
      const nameParts = displayName.split(' ');
      const firstName = nameParts[0] || 'User';
      const lastName = nameParts.slice(1).join(' ') || '';
      const profileId = profile?._id || profile?.id || profile?.rawProfile?._id || '';
      const slug = generateProfileSlug(firstName, lastName, profileId);
      const shareUrl = `${window.location.origin}/profile/${slug}`;
      const shareTitle = `${displayName} (${profile?.age || 25} yrs) | Atootbandan Matrimony`;
      const shareText = `Check out ${displayName}'s profile on Atootbandan Matrimony: ${shareUrl}`;

      if (navigator.share) {
        await navigator.share({
          title: shareTitle,
          text: shareText,
          url: shareUrl,
        });
        toast.success('Profile link shared!');
      } else {
        await navigator.clipboard.writeText(shareUrl);
        toast.success('Profile link copied to clipboard!');
      }
    } catch (err: any) {
      if (err.name !== 'AbortError') {
        toast.error('Could not share profile link');
      }
    }
  };
  const [remainingContactViews, setRemainingContactViews] = useState<number>(0);

  // Fetch current user's full profile data to get photo count
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;

        const response = await fetch(`${API_URL}/profiles/me`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          setCurrentUserProfile(data.data);
        }
      } catch (error) {
        logger.error('❌ Error fetching user profile:', error);
      }
    };

    fetchUserProfile();
  }, []);

  // Get current user's photo count for blur logic
  const currentUserPhotoCount = useMemo(() => {
    const profileToCheck = currentUserProfile;

    // Check all possible photo locations
    let photos: any[] = [];

    if (currentUserProfile?.photos && Array.isArray(currentUserProfile.photos)) {
      photos = currentUserProfile.photos;
    } else if ((profileToCheck as any)?.photos && Array.isArray((profileToCheck as any).photos)) {
      photos = (profileToCheck as any).photos;
    } else if ((profileToCheck as any)?.profile?.photos && Array.isArray((profileToCheck as any).profile.photos)) {
      photos = (profileToCheck as any).profile.photos;
    } else if (currentUserProfile?.profilePhoto) {
      photos = [currentUserProfile.profilePhoto];
    } else if ((profileToCheck as any)?.profilePhoto) {
      photos = [(profileToCheck as any).profilePhoto];
    }

    // Filter out empty/invalid photos and count real uploaded photos
    const validPhotos = photos.filter((p: any) => {
      const photoUrl = typeof p === 'string' ? p : p?.url;
      if (!photoUrl || photoUrl.trim() === '') return false;

      // Only count real uploaded photos from CDN/S3
      const isRealPhoto = (
        !photoUrl.startsWith('data:') &&
        !photoUrl.startsWith('blob:') &&
        (photoUrl.includes('cloudfront.net') ||
          photoUrl.includes('amazonaws.com') ||
          photoUrl.includes('s3.') ||
          photoUrl.includes('.jpg') ||
          photoUrl.includes('.jpeg') ||
          photoUrl.includes('.png') ||
          photoUrl.includes('.webp'))
      );

      return isRealPhoto;
    });

    return validPhotos.length;
  }, [currentUserProfile]);

  // Calculate if photos should be blurred
  const { getPhotoBlurState, currentUserPhotoCount: hookPhotoCount } = usePhotoBlur();

  // Check if current photo index should be blurred
  const blurState = getPhotoBlurState(currentPhotoIndex);
  const isCurrentPhotoBlurred = blurState.shouldBlur;

  // Extract photos
  let photos: string[] = [];

  // Try multiple sources for photos
  if (profile.photos && Array.isArray(profile.photos) && profile.photos.length > 0) {
    photos = profile.photos
      .filter((p: any) => {
        if (!p) return false;
        // Filter out explicitly non-approved photos if object
        if (typeof p === 'object') {
          // If status/isApproved exists, check it. Otherwise assume approved (legacy)
          if ('status' in p) return p.status === 'approved' || p.status === 'pending';
          if ('isApproved' in p) return p.isApproved === true;
          return true; // Legacy string/object without status
        }
        return true; // Strings are assumed valid/approved URLs from old schema
      })
      .map((p: any) => {
        if (typeof p === 'string' && p.trim() !== '') return p;
        if (p && typeof p === 'object' && p.url && typeof p.url === 'string' && p.url.trim() !== '') return p.url;
        return null;
      })
      .filter((url: any): url is string => url !== null);
  } else if (profile.rawProfile?.photos && Array.isArray(profile.rawProfile.photos) && profile.rawProfile.photos.length > 0) {
    // Try rawProfile.photos
    photos = profile.rawProfile.photos
      .filter((p: any) => {
        if (!p) return false;
        if (typeof p === 'object') {
          if ('status' in p) return p.status === 'approved' || p.status === 'pending';
          if ('isApproved' in p) return p.isApproved === true;
          return true;
        }
        return true;
      })
      .map((p: any) => {
        if (typeof p === 'string' && p.trim() !== '') return p;
        if (p && typeof p === 'object' && p.url && typeof p.url === 'string' && p.url.trim() !== '') return p.url;
        return null;
      })
      .filter((url: any): url is string => url !== null);
  } else if (profile.image && typeof profile.image === 'string' && profile.image.trim() !== '') {
    photos = [profile.image];
  } else if (profile.rawProfile?.profilePhoto && typeof profile.rawProfile.profilePhoto === 'string' && profile.rawProfile.profilePhoto.trim() !== '') {
    photos = [profile.rawProfile.profilePhoto];
  } else if (profile.rawProfile?.userId?.profilePhoto && typeof profile.rawProfile.userId.profilePhoto === 'string' && profile.rawProfile.userId.profilePhoto.trim() !== '') {
    photos = [profile.rawProfile.userId.profilePhoto];
  }

  // Fallback to empty if we have literally zero photos extracted
  if (photos.length === 0) {
    photos = [""];
  }

  // Don't add Unsplash fallback - let components handle no photos with placeholder

  // Fetch similar profiles
  useEffect(() => {
    const fetchSimilarProfiles = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;

        const response = await fetch(`${API_URL}/profiles/discovery?limit=5`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          // Backend error - just skip similar profiles
          logger.warn('Similar profiles API error:', response.status);
          return;
        }

        const data = await response.json();
        if (data.success && data.data?.profiles) {
          setSimilarProfiles(data.data.profiles.slice(0, 3));
        }
      } catch (error) {
        // Silent fail - similar profiles are not critical
        logger.warn('Failed to fetch similar profiles:', error);
      }
    };

    fetchSimilarProfiles();
  }, []);

  // Check interaction status with this profile
  useEffect(() => {
    const checkInteractionStatus = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;

        const profileId = profile.id || profile._id;
        if (!profileId) return;

        logger.log('🔍 Checking interaction status for profile:', profileId);

        // Check both sent AND received interactions
        const [sentResponse, receivedResponse] = await Promise.all([
          fetch(`${API_URL}/interactions/sent?type=interest,superlike,shortlist`, {
            headers: { 'Authorization': `Bearer ${token}` }
          }),
          fetch(`${API_URL}/interactions/received?type=interest,superlike,shortlist`, {
            headers: { 'Authorization': `Bearer ${token}` }
          })
        ]);

        // Check sent interactions first
        if (sentResponse.ok) {
          const sentData = await sentResponse.json();
          logger.log('📤 Sent interactions:', sentData);
          if (sentData.success) {
            const interactions = sentData.data?.interactions || sentData.data || [];
            const interaction = interactions.find((int: any) => {
              const matchesProfile = (
                int.toProfile?._id === profileId ||
                int.toProfile === profileId ||
                int.toUser?._id === profileId ||
                int.toUser === profileId
              );
              logger.log('Checking sent interaction:', {
                interactionId: int._id,
                toProfile: int.toProfile?._id || int.toProfile,
                toUser: int.toUser?._id || int.toUser,
                status: int.status,
                matchesProfile
              });
              return matchesProfile;
            });

            if (interaction) {
              logger.log('✅ Found in SENT interactions:', interaction);
              setInteractionStatus({
                status: interaction.status,
                interactionId: interaction._id
              });
              // Set shortlisted state if interaction type is shortlist
              if (interaction.status === 'shortlist') {
                setIsShortlisted(true);
              }
              return; // Found in sent, no need to check received
            }
          }
        }

        // Check received interactions if not found in sent
        if (receivedResponse.ok) {
          const receivedData = await receivedResponse.json();
          logger.log('📥 Received interactions:', receivedData);
          if (receivedData.success) {
            const interactions = receivedData.data?.interactions || receivedData.data || [];
            const interaction = interactions.find((int: any) => {
              const matchesProfile = (
                int.fromProfile?._id === profileId ||
                int.fromProfile === profileId ||
                int.fromUser?._id === profileId ||
                int.fromUser === profileId
              );
              logger.log('Checking received interaction:', {
                interactionId: int._id,
                fromProfile: int.fromProfile?._id || int.fromProfile,
                fromUser: int.fromUser?._id || int.fromUser,
                status: int.status,
                matchesProfile
              });
              return matchesProfile;
            });

            if (interaction) {
              logger.log('✅ Found in RECEIVED interactions:', interaction);
              setInteractionStatus({
                status: interaction.status,
                interactionId: interaction._id
              });
            } else {
              logger.log('❌ No interaction found with this profile');
            }
          }
        }
      } catch (error) {
        logger.error('❌ Failed to check interaction status:', error);
      }
    };

    checkInteractionStatus();
  }, [profile]);

  const handleLike = async () => {
    // Optimistic Update
    const previousStatus = interactionStatus;
    setInteractionStatus({ status: 'interest', interactionId: 'optimistic-id' });
    toast.success('Interest sent successfully!'); // Immediate feedback

    try {
      setIsLiking(true);
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Please login to send interest');
        // Revert
        setInteractionStatus(previousStatus);
        return;
      }

      const response = await fetch(`${API_URL}/interactions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          profileId: profile.id || profile._id,
          type: 'interest'
        })
      });

      const data = await response.json();

      if (data.success) {
        if (data.data?.isMatch) {
          toast.success("It's a Match! 💕", {
            description: 'You can now start chatting!',
            duration: 5000
          });
        }
        // Update with real ID
        setInteractionStatus({ status: 'interest', interactionId: data.data.interactionId });
      } else {
        // Revert
        setInteractionStatus(previousStatus);
        toast.error(data.message || 'Failed to send interest');
      }
    } catch (error: any) {
      // Revert
      setInteractionStatus(previousStatus);
      toast.error(error.message || 'Failed to send interest');
    } finally {
      setIsLiking(false);
    }
  };

  const handleShortlist = async () => {
    // Optimistic Update
    const previousState = isShortlisted;
    setIsShortlisted(!isShortlisted);
    toast.success(!isShortlisted ? 'Added to shortlist' : 'Removed from shortlist');

    try {
      setIsShortlisting(true);
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Please login to shortlist profiles');
        setIsShortlisted(previousState); // Revert
        return;
      }

      const response = await fetch(`${API_URL}/interactions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          profileId: profile.id || profile._id,
          type: 'shortlist'
        })
      });

      const data = await response.json();

      if (data.success) {
        // Confirmed state
        // setIsShortlisted is already set optimistically
        setInteractionStatus({ status: 'shortlist', interactionId: data.data.interactionId });
      } else {
        // Revert
        setIsShortlisted(previousState);
        toast.error(data.message || 'Failed to update shortlist');
      }
    } catch (error: any) {
      // Revert
      setIsShortlisted(previousState);
      toast.error(error.message || 'Failed to update shortlist');
    } finally {
      setIsShortlisting(false);
    }
  };

  const handleRevealContact = async () => {
    try {
      setRevealingContact(true);
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Please login to view contact details');
        return;
      }

      const profileId = profile.id || profile._id;
      if (!profileId) {
        toast.error('Profile ID not found');
        return;
      }

      const response = await fetch(`${API_URL}/contact-reveal/${profileId}/contact`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      // Check if response is actually JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error('Server returned an invalid response. Please try again later.');
      }

      const data = await response.json();

      if (data.success) {
        setContactRevealed(true);
        setContactInfo(data.data);

        if (!data.data.alreadyRevealed) {
          const remainingViews = data.data.contactViewsRemaining || 0;
          setRemainingContactViews(remainingViews);
          toast.success(`Contact revealed! ${remainingViews} views remaining.`);
        } else {
          toast.success('Contact details retrieved');
        }
      } else {
        // Show modal instead of toast for plan/subscription errors
        const errorMessage = data.message || 'Failed to reveal contact';

        // Check if it's a subscription/plan related error
        const isPlanError = errorMessage.toLowerCase().includes('subscription') ||
          errorMessage.toLowerCase().includes('views') ||
          errorMessage.toLowerCase().includes('plan') ||
          errorMessage.toLowerCase().includes('upgrade') ||
          errorMessage.toLowerCase().includes('contact');

        if (isPlanError) {
          // Show beautiful modal instead of toast
          setContactErrorMessage(errorMessage);
          setRemainingContactViews(data.data?.contactViewsRemaining || 0);
          setShowContactUpgradeModal(true);
        } else {
          toast.error(errorMessage);
        }
      }
    } catch (error: any) {
      logger.error('❌ [ViewProfilePageNew] Contact reveal error:', error);
      const errorMsg = error.message || 'Failed to reveal contact';

      // Check if it's a subscription error and show modal
      const isPlanError = errorMsg.toLowerCase().includes('subscription') ||
        errorMsg.toLowerCase().includes('plan') ||
        errorMsg.toLowerCase().includes('views') ||
        errorMsg.toLowerCase().includes('contact');

      if (isPlanError) {
        setContactErrorMessage(errorMsg);
        setShowContactUpgradeModal(true);
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setRevealingContact(false);
    }
  };

  const handleCancelInterest = async () => {
    if (!interactionStatus?.interactionId) return;

    try {
      setIsLiking(true);
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Please login first');
        return;
      }

      const response = await fetch(`${API_URL}/interactions/${interactionStatus.interactionId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Interest cancelled');
        setInteractionStatus(null);
      } else {
        toast.error(data.message || 'Failed to cancel interest');
      }
    } catch (error: any) {
      toast.error('Failed to cancel interest');
    } finally {
      setIsLiking(false);
    }
  };

  const handleChatNow = () => {
    // Navigate to chat with this profile
    const userId = profile.userId?._id || profile.userId || profile.rawProfile?.userId?._id || profile.id || profile._id;

    if (!userId) {
      toast.error('Unable to open chat. User ID not found.');
      return;
    }

    if (onOpenChat) {
      onOpenChat(userId);
    } else {
      // Fallback: navigate to /app and set the active tab to messages
      // Store the userId to open chat in localStorage temporarily
      localStorage.setItem('openChatWithUser', userId);
      window.location.href = '/app?tab=messages';
    }
  };

  const handleSubmitReport = async (reason: string, additionalInfo: string) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Please login to report users');
        return;
      }

      const profileId = profile.id || profile._id;
      const response = await fetch(`${API_URL}/users/report`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          reportedUserId: profileId,
          reason,
          details: additionalInfo
        })
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Report submitted successfully');
        setIsReportModalOpen(false);
      } else {
        toast.error(data.message || 'Failed to submit report');
      }
    } catch (error: any) {
      toast.error('Failed to submit report');
      throw error;
    }
  };

  // Calculate match compatibility (mock for now)
  const matchCompatibility = 85;

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-purple-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-lg border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-gradient-to-r hover:from-pink-50 hover:to-purple-50 rounded-lg transition-all duration-200 group"
            >
              <ArrowLeft className="size-5 group-hover:text-rose-600 transition-colors" />
            </button>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-xl bg-gradient-to-r from-rose-600 to-purple-600 bg-clip-text text-transparent">Profile Details</h1>
                {/* Premium Badge */}
                {(profile.isPro || profile.rawProfile?.isPremium || profile.rawProfile?.activePlan) && (
                  <div className="bg-gradient-to-r from-yellow-400 to-yellow-600 text-white px-2 py-1 rounded-full text-xs flex items-center gap-1">
                    <Crown className="size-3" />
                    Premium
                  </div>
                )}
              </div>
              <p className="text-xs text-gray-500">Home › Matches › Profile Details</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleShareProfile}
              className="flex items-center gap-1.5 text-xs font-semibold border-rose-200 text-rose-600 hover:bg-rose-50 rounded-full px-3.5 py-1.5 shadow-sm transition-all"
            >
              <Share2 className="size-3.5" />
              Share Profile
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Main Content (2/3 width) */}
          <div className="lg:col-span-2 space-y-6">
            {/* Profile Card with Photo and Basic Info */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="grid grid-cols-1 md:grid-cols-5 gap-0">
                {/* Photo Section */}
                <div className="md:col-span-2 relative bg-gradient-to-br from-gray-50 to-gray-100">
                  <div
                    className="relative h-[400px] bg-gray-100 flex items-center justify-center cursor-pointer group"
                    onClick={() => setShowImageViewer(true)}
                  >
                    <ImageWithFallback
                      src={photos[currentPhotoIndex]}
                      alt={profile.name}
                      className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
                      style={isCurrentPhotoBlurred ? { filter: 'blur(20px)' } : undefined}
                    />
                    {isCurrentPhotoBlurred && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/10">
                        <div className="bg-white/95 backdrop-blur-sm px-6 py-3 rounded-lg text-center">
                          <p className="text-sm font-medium text-gray-900">
                            {currentUserPhotoCount === 0 ? 'Upload 1 photo' : `Upload ${currentUserPhotoCount === 1 ? '1 more' : currentUserPhotoCount === 2 ? '1 more' : '1 more'}`}
                          </p>
                          <p className="text-xs text-gray-600">to view this photo</p>
                        </div>
                      </div>
                    )}
                    {!isCurrentPhotoBlurred && (
                      <>
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all duration-300" />
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <div className="bg-white/90 backdrop-blur-sm rounded-full p-3">
                            <Eye className="size-6 text-rose-500" />
                          </div>
                        </div>
                      </>
                    )}
                    {photos.length > 1 && (
                      <div className="absolute bottom-3 left-3 bg-black/70 backdrop-blur-sm text-white px-3 py-1.5 rounded-full text-xs flex items-center gap-1">
                        <Eye className="size-3" />
                        {photos.length} Photos
                      </div>
                    )}
                    {profile.isOnline && (
                      <div className="absolute top-3 right-3 bg-green-500 text-white px-2 py-1 rounded-full text-[10px] flex items-center gap-1">
                        <div className="size-1.5 bg-white rounded-full"></div>
                        Online
                      </div>
                    )}
                  </div>

                  {/* Photo Thumbnails */}
                  {photos.length > 1 && (
                    <div className="p-3 bg-white border-t border-gray-200">
                      <div className="flex gap-2 overflow-x-auto">
                        {photos.slice(0, 5).map((photo: string, idx: number) => {
                          const thumbnailBlurState = getPhotoBlurState(idx);
                          return (
                            <button
                              key={idx}
                              onClick={() => setCurrentPhotoIndex(idx)}
                              className={`relative flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all hover:border-rose-400 ${currentPhotoIndex === idx ? 'border-rose-500' : 'border-gray-200'
                                }`}
                            >
                              <ImageWithFallback
                                src={photo}
                                alt={`Thumbnail ${idx + 1}`}
                                className="w-full h-full object-cover"
                                style={thumbnailBlurState.shouldBlur ? { filter: 'blur(8px)' } : undefined}
                              />
                              {thumbnailBlurState.shouldBlur && (
                                <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                                  <svg className="size-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                  </svg>
                                </div>
                              )}
                            </button>
                          );
                        })}
                        {photos.length > 5 && (
                          <button className="flex-shrink-0 w-16 h-16 rounded-lg bg-gray-100 flex items-center justify-center text-xs border-2 border-gray-200">
                            +{photos.length - 5}
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Profile Info Section */}
                <div className="md:col-span-3 p-6">
                  {/* Name and Verified Badge */}
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h2 className="text-2xl">{profile.name.split(' ')[0]} {profile.name.split(' ')[1]?.charAt(0)}.</h2>
                        {profile.isPro && (
                          <div className="bg-green-500 text-white px-2 py-0.5 rounded text-xs flex items-center gap-1">
                            <Shield className="size-3" />
                            VERIFIED
                          </div>
                        )}
                      </div>
                      <p className="text-sm text-gray-600">Profile created by {profile.profileManaged}</p>
                    </div>
                  </div>

                  {/* Key Info Grid - 2 columns */}
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <Calendar className="size-4 text-gray-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-xs text-gray-500">Age & Height</p>
                        <p className="text-sm">{profile.age} Yrs, {formatHeightDisplay(profile.height)}</p>
                      </div>
                    </div>

                    {profile.maritalStatus && (
                      <div className="flex items-start gap-3">
                        <User className="size-4 text-gray-400 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-xs text-gray-500">Marital Status</p>
                          <p className="text-sm">{formatMaritalStatus(profile.maritalStatus)}</p>
                        </div>
                      </div>
                    )}

                    <div className="flex items-start gap-3">
                      <MapPin className="size-4 text-gray-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-xs text-gray-500">Location</p>
                        <p className="text-sm">
                          {profile.city && profile.state
                            ? `${profile.city}, ${profile.state}`
                            : profile.location}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <GraduationCap className="size-4 text-gray-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-xs text-gray-500">Education</p>
                        <p className="text-sm">{formatEducation(profile.education)}</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <Briefcase className="size-4 text-gray-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-xs text-gray-500">Profession</p>
                        <p className="text-sm">{profile.occupation}</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <Home className="size-4 text-gray-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-xs text-gray-500">Religion</p>
                        <p className="text-sm">{profile.religion}{profile.community ? ` • ${profile.community}` : ''}</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <Users className="size-4 text-gray-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-xs text-gray-500">Mother Tongue</p>
                        <p className="text-sm">{profile.motherTongue || 'Not specified'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Status Badges */}
                  {/* <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-gray-200">
                    <Badge variant="secondary" className="text-xs bg-green-50 text-green-700 border-green-200">
                      Active 2h ago
                    </Badge>
                    <Badge variant="secondary" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                      {profile.maritalStatus || 'Never Married'}
                    </Badge>
                    {profile.diet && (
                      <Badge variant="secondary" className="text-xs bg-orange-50 text-orange-700 border-orange-200">
                        {profile.diet}
                      </Badge>
                    )}
                  </div> */}
                </div>
              </div>
            </div>

            {/* About Section */}
            <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
              <h3 className="text-lg mb-3 flex items-center gap-2">
                <User className="size-5 text-rose-500" />
                About {profile.name.split(' ')[0]}
              </h3>
              <p className="text-gray-700 text-sm leading-relaxed break-words word-wrap-break-word overflow-wrap-anywhere max-w-full">
                {profile.about || `Hello, thanks for visiting my profile. I have completed my ${profile.education} and currently working as ${profile.occupation}. I am a fun-loving, ambitious, and down-to-earth person who values family traditions while embracing modern thinking. In my free time, I enjoy reading classic literature, traveling to hill stations, and trying out new cuisines. I am looking for a partner who is understanding, professionally settled, and willing to support my career aspirations.`}
              </p>
            </div>

            {/* Education & Career + Family Side by Side */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Education & Career */}
              <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
                <h3 className="text-lg mb-4 flex items-center gap-2">
                  <GraduationCap className="size-5 text-rose-500" />
                  Education & Career
                </h3>
                <div className="space-y-4">
                  <div>
                    <p className="text-xs text-gray-500 uppercase mb-1">Highest Education</p>
                    <p className="text-sm">{formatEducation(profile.education)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase mb-1">College</p>
                    <p className="text-sm">{profile.company}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase mb-1">Employed In</p>
                    <p className="text-sm">Private Sector</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase mb-1">Occupation</p>
                    <p className="text-sm">{profile.occupation}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase mb-1">Annual Income</p>
                    <p className="text-sm">{profile.income}</p>
                  </div>
                </div>
              </div>

              {/* Family Background */}
              <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
                <h3 className="text-lg mb-4 flex items-center gap-2">
                  <Users className="size-5 text-rose-500" />
                  Family Background
                </h3>
                <div className="space-y-4">
                  <div>
                    <p className="text-xs text-gray-500 uppercase mb-1">Father's Name</p>
                    <p className="text-sm">{profile.rawProfile?.family?.fatherName || profile.fatherName || 'Not specified'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase mb-1">Father's Occupation</p>
                    <p className="text-sm">{profile.rawProfile?.family?.fatherOccupation || profile.fatherOccupation || 'Not specified'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase mb-1">Mother's Name</p>
                    <p className="text-sm">{profile.rawProfile?.family?.motherName || profile.motherName || 'Not specified'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase mb-1">Mother's Occupation</p>
                    <p className="text-sm">{profile.rawProfile?.family?.motherOccupation || profile.motherOccupation || 'Not specified'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase mb-1">Siblings</p>
                    <p className="text-sm">
                      {(() => {
                        const siblings = profile.rawProfile?.family?.siblings || profile.siblings;
                        if (siblings) {
                          if (typeof siblings === 'string') return siblings;
                          if (typeof siblings === 'object' && siblings.brothers !== undefined && siblings.sisters !== undefined) {
                            const brothers = siblings.brothers || 0;
                            const sisters = siblings.sisters || 0;
                            if (brothers === 0 && sisters === 0) return 'No siblings';
                            const parts = [];
                            if (brothers > 0) parts.push(`${brothers} Brother${brothers > 1 ? 's' : ''}`);
                            if (sisters > 0) parts.push(`${sisters} Sister${sisters > 1 ? 's' : ''}`);
                            return parts.join(', ');
                          }
                        }
                        return 'Not specified';
                      })()}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase mb-1">Family Type</p>
                    <p className="text-sm">{profile.rawProfile?.family?.familyType || profile.familyType || 'Not specified'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase mb-1">Family Status</p>
                    <p className="text-sm">{profile.rawProfile?.family?.familyStatus || profile.familyStatus || 'Not specified'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase mb-1">Family Values</p>
                    <p className="text-sm">{profile.rawProfile?.family?.familyValues || profile.familyValues || 'Not specified'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase mb-1">Family Location</p>
                    <p className="text-sm">{profile.location}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Partner Preferences */}
            <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg flex items-center gap-2">
                  <Heart className="size-5 text-rose-500" />
                  Partner Preferences
                </h3>
              </div>

              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left py-3 px-4 text-xs text-gray-500 uppercase">Criteria</th>
                      <th className="text-left py-3 px-4 text-xs text-gray-500 uppercase">Preference</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {(() => {
                      const prefs = profile.partnerPreferences || profile.rawProfile?.partnerPreferences;
                      const ageRange = prefs?.ageRange;
                      const heightRange = prefs?.heightRange;
                      const maritalStatus = prefs?.maritalStatus;
                      const religion = prefs?.religion;
                      const community = prefs?.community;
                      const motherTongue = prefs?.motherTongue;
                      const diet = prefs?.diet;
                      const smoking = prefs?.smoking;
                      const drinking = prefs?.drinking;
                      const education = prefs?.education;
                      const occupation = prefs?.occupation;

                      return (
                        <>
                          {ageRange && (ageRange.min || ageRange.max) ? (
                            <tr className="hover:bg-gray-50">
                              <td className="py-3 px-4">Age</td>
                              <td className="py-3 px-4">
                                {ageRange.min && ageRange.max
                                  ? `${ageRange.min} to ${ageRange.max} Yrs`
                                  : ageRange.min
                                    ? `${ageRange.min}+ Yrs`
                                    : `Up to ${ageRange.max} Yrs`}
                              </td>
                            </tr>
                          ) : (
                            <tr className="hover:bg-gray-50">
                              <td className="py-3 px-4">Age</td>
                              <td className="py-3 px-4 text-gray-400">Not specified</td>
                            </tr>
                          )}
                          {heightRange && (heightRange.min || heightRange.max) ? (
                            <tr className="hover:bg-gray-50">
                              <td className="py-3 px-4">Height</td>
                              <td className="py-3 px-4">
                                {heightRange.min && heightRange.max
                                  ? `${formatHeightDisplay(heightRange.min)} to ${formatHeightDisplay(heightRange.max)}`
                                  : heightRange.min
                                    ? `${formatHeightDisplay(heightRange.min)}+`
                                    : `Up to ${formatHeightDisplay(heightRange.max)}`}
                              </td>
                            </tr>
                          ) : (
                            <tr className="hover:bg-gray-50">
                              <td className="py-3 px-4">Height</td>
                              <td className="py-3 px-4 text-gray-400">Not specified</td>
                            </tr>
                          )}
                          {maritalStatus && maritalStatus.length > 0 ? (
                            <tr className="hover:bg-gray-50">
                              <td className="py-3 px-4">Marital Status</td>
                              <td className="py-3 px-4">{maritalStatus.map(formatMaritalStatus).join(', ')}</td>
                            </tr>
                          ) : (
                            <tr className="hover:bg-gray-50">
                              <td className="py-3 px-4">Marital Status</td>
                              <td className="py-3 px-4 text-gray-400">Not specified</td>
                            </tr>
                          )}
                          {religion && religion.length > 0 && (
                            <tr className="hover:bg-gray-50">
                              <td className="py-3 px-4">Religion</td>
                              <td className="py-3 px-4">{religion.join(', ')}</td>
                            </tr>
                          )}
                          {community && community.length > 0 && (
                            <tr className="hover:bg-gray-50">
                              <td className="py-3 px-4">Community</td>
                              <td className="py-3 px-4">{community.slice(0, 10).join(', ')}{community.length > 10 ? `, +${community.length - 10} more` : ''}</td>
                            </tr>
                          )}
                          {motherTongue && motherTongue.length > 0 && (
                            <tr className="hover:bg-gray-50">
                              <td className="py-3 px-4">Mother Tongue</td>
                              <td className="py-3 px-4">{motherTongue.join(', ')}</td>
                            </tr>
                          )}
                          {diet && diet.length > 0 && (
                            <tr className="hover:bg-gray-50">
                              <td className="py-3 px-4">Diet</td>
                              <td className="py-3 px-4">{diet.join(', ')}</td>
                            </tr>
                          )}
                          {smoking && smoking.length > 0 && (
                            <tr className="hover:bg-gray-50">
                              <td className="py-3 px-4">Smoking</td>
                              <td className="py-3 px-4">{smoking.join(', ')}</td>
                            </tr>
                          )}
                          {drinking && drinking.length > 0 && (
                            <tr className="hover:bg-gray-50">
                              <td className="py-3 px-4">Drinking</td>
                              <td className="py-3 px-4">{drinking.join(', ')}</td>
                            </tr>
                          )}
                          {education && education.length > 0 && (
                            <tr className="hover:bg-gray-50">
                              <td className="py-3 px-4">Education</td>
                              <td className="py-3 px-4">{education.join(', ')}</td>
                            </tr>
                          )}
                          {occupation && occupation.length > 0 && (
                            <tr className="hover:bg-gray-50">
                              <td className="py-3 px-4">Occupation</td>
                              <td className="py-3 px-4">{occupation.join(', ')}</td>
                            </tr>
                          )}
                        </>
                      );
                    })()}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Right Column - Sidebar (1/3 width) */}
          <div className="lg:col-span-1 space-y-4">
            {/* Match Compatibility Card */}
            <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm sticky top-24">
              <div className="text-center mb-4">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm">Match Compatibility</p>
                  <p className="text-2xl text-rose-500">{matchCompatibility}%</p>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                  <div
                    className="bg-rose-500 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${matchCompatibility}%` }}
                  ></div>
                </div>
                <p className="text-xs text-gray-500">Excellent match based on your preferences</p>
              </div>

              {/* Connect/Cancel/Chat Button - based on interaction status */}
              {!hideInterestButtons && (
                <>
                  {interactionStatus?.status === 'declined' ? (
                    // Show Interest Declined button for declined interest
                    <Button
                      disabled
                      className="w-full bg-gray-400 text-white cursor-not-allowed py-3 rounded-lg mb-3"
                    >
                      <X className="size-4 mr-2" />
                      Interest Declined
                    </Button>
                  ) : interactionStatus?.status === 'pending' ? (
                    // Show Cancel button for pending interest
                    <Button
                      onClick={handleCancelInterest}
                      disabled={isLiking}
                      className="w-full bg-red-50 hover:bg-red-100 text-red-600 border border-red-200 py-3 rounded-lg mb-3"
                    >
                      <X className="size-4 mr-2" />
                      {isLiking ? 'Cancelling...' : 'Cancel Interest'}
                    </Button>
                  ) : interactionStatus?.status === 'accepted' ? (
                    // Show Chat Now button for accepted interest
                    <Button
                      onClick={handleChatNow}
                      className="w-full bg-green-600 hover:bg-green-700 text-white py-3 rounded-lg mb-3"
                    >
                      <MessageCircle className="size-4 mr-2" />
                      Chat Now
                    </Button>
                  ) : (
                    // Show Connect Now button if no interaction
                    <Button
                      onClick={handleLike}
                      disabled={isLiking}
                      className="w-full bg-rose-500 hover:bg-rose-600 text-white py-3 rounded-lg mb-3"
                    >
                      <Check className="size-4 mr-2" />
                      {isLiking ? 'Sending...' : 'Connect Now'}
                    </Button>
                  )}
                </>
              )}

              {/* Action Buttons */}
              <div className="grid grid-cols-1 gap-2 mb-4">
                <Button
                  variant={isShortlisted ? "default" : "outline"}
                  onClick={handleShortlist}
                  disabled={isShortlisting}
                  className={`text-sm ${isShortlisted ? 'bg-yellow-500 hover:bg-yellow-600 text-white border-yellow-500' : 'border-gray-300'}`}
                >
                  <Star className={`size-4 mr-1 ${isShortlisted ? 'fill-current' : ''}`} />
                  {isShortlisting ? 'Updating...' : isShortlisted ? 'Shortlisted' : 'Shortlist'}
                </Button>
              </div>

              {/* Secondary Actions */}
              {/* <div className="flex items-center justify-center gap-4 text-xs text-gray-500 pt-3 border-t border-gray-200">
                <button className="flex items-center gap-1 hover:text-gray-700" onClick={() => setIsReportModalOpen(true)}>
                  <Flag className="size-3" />
                  Report
                </button>
              </div> */}
            </div>

            {/* Contact Details Card */}
            <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
              <h3 className="text-sm mb-3 flex items-center gap-2">
                <Phone className="size-4 text-rose-500" />
                Contact Details
              </h3>
              {contactRevealed ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="size-4 text-gray-400" />
                    <span>{contactInfo?.phone || 'Not available'}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="size-4 text-gray-400" />
                    <span className="truncate">{contactInfo?.email || 'Not available'}</span>
                  </div>
                </div>
              ) : (
                <div>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-3 text-center">
                    <Lock className="size-6 text-green-600 mx-auto mb-1" />
                    <p className="text-xs text-green-700">
                      Available to connected members
                    </p>
                  </div>
                  <Button
                    onClick={handleRevealContact}
                    disabled={revealingContact}
                    variant="outline"
                    className="w-full text-sm"
                  >
                    {revealingContact ? 'Revealing...' : 'View Contact'}
                  </Button>
                </div>
              )}
            </div>

            {/* Similar Profiles */}
            {similarProfiles.length > 0 && (
              <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
                <h3 className="text-sm mb-3 flex items-center gap-2 uppercase">
                  Similar Profiles
                </h3>
                <div className="space-y-3">
                  {similarProfiles.map((prof: any) => {
                    const firstName = prof.userId?.firstName || prof.firstName || 'User';
                    const lastName = prof.userId?.lastName || prof.lastName || '';
                    const age = prof.age || (prof.dateOfBirth ? new Date().getFullYear() - new Date(prof.dateOfBirth).getFullYear() : 25);
                    const heightDisplay = formatHeightDisplay(prof.height || 165);
                    const city = prof.city || prof.userId?.city || 'Mumbai';
                    const occupation = prof.occupation || prof.userId?.occupation || 'Professional';
                    const photoUrl = prof.photos?.[0]?.url || prof.photos?.[0] || prof.profilePhoto || `https://ui-avatars.com/api/?name=${firstName}+${lastName}&background=f43f5e&color=fff`;

                    return (
                      <div
                        key={prof._id}
                        className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 p-2 rounded-lg transition-colors"
                        onClick={() => {
                          if (onViewProfile) {
                            onViewProfile(prof);
                          }
                        }}
                      >
                        <div className="size-12 rounded-lg overflow-hidden flex-shrink-0">
                          <ImageWithFallback
                            src={photoUrl}
                            alt={`${firstName} ${lastName}`}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm truncate font-medium">{firstName} {lastName?.charAt(0)}.</p>
                          <p className="text-xs text-gray-500 truncate">
                            {age} Yrs, {heightDisplay} - {city}
                          </p>
                          <p className="text-xs text-gray-500 truncate">{occupation}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onViewAllSimilar) {
                      onViewAllSimilar();
                    }
                  }}
                  className="w-full mt-3 py-2 text-sm text-rose-500 hover:bg-rose-50 rounded-lg transition-colors font-medium"
                >
                  View All Matches
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Image Viewer Modal */}
      {showImageViewer && (
        <div
          className="fixed inset-0 bg-black/95 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
          onClick={() => setShowImageViewer(false)}
        >
          <button
            onClick={() => setShowImageViewer(false)}
            className="absolute top-4 right-4 bg-white/10 hover:bg-white/20 text-white rounded-full p-3 transition-colors z-10"
          >
            <X className="size-6" />
          </button>

          {/* Image Navigation */}
          {photos.length > 1 && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrentPhotoIndex((prev) => (prev > 0 ? prev - 1 : photos.length - 1));
                }}
                className="absolute left-4 bg-white/10 hover:bg-white/20 text-white rounded-full p-3 transition-colors z-10"
              >
                <ChevronLeft className="size-6" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrentPhotoIndex((prev) => (prev < photos.length - 1 ? prev + 1 : 0));
                }}
                className="absolute right-4 bg-white/10 hover:bg-white/20 text-white rounded-full p-3 transition-colors z-10"
              >
                <ChevronRight className="size-6" />
              </button>
            </>
          )}

          {/* Main Image */}
          <div className="relative max-w-4xl w-full h-[80vh] flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
            <ImageWithFallback
              src={photos[currentPhotoIndex]}
              alt={profile.name}
              className="w-full h-full object-contain"
              style={getPhotoBlurState(currentPhotoIndex).shouldBlur ? { filter: 'blur(25px)' } : undefined}
            />

            {/* Blur Overlay with Message */}
            {getPhotoBlurState(currentPhotoIndex).shouldBlur && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                <div className="bg-white/95 backdrop-blur-sm px-8 py-6 rounded-2xl text-center shadow-2xl max-w-sm">
                  <div className="size-16 rounded-full bg-rose-100 flex items-center justify-center mx-auto mb-4">
                    <svg className="size-8 text-rose-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <p className="text-lg font-semibold text-gray-900 mb-2">
                    {currentUserPhotoCount === 0 ? 'Upload 1 photo' : `Upload ${currentPhotoIndex - currentUserPhotoCount + 1} more photo${currentPhotoIndex - currentUserPhotoCount + 1 > 1 ? 's' : ''}`}
                  </p>
                  <p className="text-sm text-gray-600">to view more photos</p>
                </div>
              </div>
            )}

            {/* Image Counter */}
            {photos.length > 1 && (
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/70 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm">
                {currentPhotoIndex + 1} / {photos.length}
              </div>
            )}
          </div>

          {/* Thumbnail Strip */}
          {photos.length > 1 && (
            <div className="absolute bottom-20 left-1/2 -translate-x-1/2 flex gap-2 p-2 bg-black/50 backdrop-blur-sm rounded-lg" onClick={(e) => e.stopPropagation()}>
              {photos.map((photo: string, idx: number) => {
                const thumbnailBlurState = getPhotoBlurState(idx);
                return (
                  <button
                    key={idx}
                    onClick={() => setCurrentPhotoIndex(idx)}
                    className={`relative w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${currentPhotoIndex === idx ? 'border-rose-500 scale-110' : 'border-white/30 hover:border-white/60'
                      }`}
                  >
                    <ImageWithFallback
                      src={photo}
                      alt={`Thumbnail ${idx + 1}`}
                      className="w-full h-full object-cover"
                      style={thumbnailBlurState.shouldBlur ? { filter: 'blur(8px)' } : undefined}
                    />
                    {thumbnailBlurState.shouldBlur && (
                      <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                        <svg className="size-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Report User Modal */}
      <ReportUserModal
        isOpen={isReportModalOpen}
        onClose={() => setIsReportModalOpen(false)}
        onSubmit={handleSubmitReport}
        userName={profile.name}
      />

      {/* Contact Upgrade Modal */}
      <ContactUpgradeModal
        isOpen={showContactUpgradeModal}
        onClose={() => setShowContactUpgradeModal(false)}
        onUpgrade={() => {
          if (onNavigateToPlans) {
            onNavigateToPlans();
          }
        }}
        errorMessage={contactErrorMessage}
        remainingViews={remainingContactViews}
      />
    </div>
  );
}